import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";
import {
  ArrowLeft, ArrowRight, CheckCircle2, Circle, AlertTriangle, ExternalLink, Loader2, RefreshCw, Shirt, Gift, Trash2, HelpCircle, Check, Clock,
} from "lucide-react";
import { StudyStep } from "../components/custom/StudyStep";
import { TemplateStep } from "../components/custom/TemplateStep";
import { SamplesStep } from "../components/custom/SamplesStep";
import { ImagesStep } from "../components/custom/ImagesStep";
import { ListingStep } from "../components/custom/ListingStep";
import { PublishStep } from "../components/custom/PublishStep";
import {
  customApi, STEP_META, STEP_ORDER, STATUS_LABEL, QUESTION_TYPE_LABEL,
  type CustomListingDetail, type StepKey, type StepState,
} from "../lib/customListing";

// Custom Liste Studyosu: bir custom urunun 7 adimi tek sayfada. Her adimda ne
// yapildigi, neden onemli oldugu, eksikler ve tek bir sonraki eylem gorunur.
// 1. asamada 2-5. adimlarin isi hala analiz sayfasindaki Custom Tasarim panelinde;
// studyo durumu gosterir ve oraya yonlendirir.


const MAX_IMAGES = 6;

export default function CustomStudioPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: rec, isLoading, error } = useQuery({
    queryKey: ["custom-listing", id],
    queryFn: () => customApi<CustomListingDetail>("GET", `/${id}`),
    // AI calismasi arka planda: bitene kadar 5 sn'de bir yenile
    refetchInterval: (q) => {
      const d = q.state.data as CustomListingDetail | undefined;
      const t = d?.template;
      const busy = d?.study?.status === "running" || t?.base?.status === "running" || t?.pkg?.status === "running" || t?.collage?.status === "running" || t?.samples?.status === "running" || t?.samplePhotos?.status === "running" || t?.iconSet?.status === "running" || (t?.blankMockups ?? []).some((b) => b.status === "running") || (t?.niches?.items ?? []).some((n) => n.status === "running")
        || Object.values(t?.glyphSets ?? {}).some((g) => g.status === "running");
      return busy ? 5000 : false;
    },
  });
  const [active, setActive] = useState<StepKey | null>(null);
  useEffect(() => { if (rec && !active) setActive(rec.current); }, [rec, active]);

  const refresh = () => qc.invalidateQueries({ queryKey: ["custom-listing", id] });
  const remove = useMutation({
    mutationFn: () => customApi("DELETE", `/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["custom-listings"] }); navigate("/custom"); },
  });

  if (isLoading) return <div className="flex items-center justify-center py-20 text-gray-400"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Yükleniyor…</div>;
  if (error || !rec) return <div className="mx-auto max-w-3xl p-6"><p className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-600">{(error as Error)?.message ?? "Kayıt bulunamadı"}</p></div>;

  const step = rec.steps.find((s) => s.key === (active ?? rec.current))!;
  const idx = STEP_ORDER.indexOf(step.key);

  return (
    <div className="mx-auto max-w-7xl p-6">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Link to="/custom" className="mb-2 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"><ArrowLeft className="h-3.5 w-3.5" /> Custom Listeler</Link>
          <h1 className="flex items-center gap-2 text-xl font-bold text-gray-900">{rec.isGarment === false ? <Gift className="h-5 w-5 flex-shrink-0 text-indigo-600" /> : <Shirt className="h-5 w-5 flex-shrink-0 text-indigo-600" />}<span className="truncate">{rec.name}</span></h1>
          <p className="mt-1 text-sm text-gray-500">
            {rec.analysis?.productType}
            {rec.analysis?.url && <> · <a href={rec.analysis.url.split("#")[0]} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-indigo-600 hover:underline">İlham listesi <ExternalLink className="h-3 w-3" /></a></>}
            {rec.analysis && <> · <Link to={`/analyses/${rec.analysis.id}`} className="text-indigo-600 hover:underline">Analiz</Link></>}
          </p>
        </div>
        <button onClick={() => { if (confirm("Stüdyo kaydı silinsin mi? Analiz, tasarımlar ve taslaklar silinmez.")) remove.mutate(); }}
          title="Stüdyo kaydını sil" aria-label="Stüdyo kaydını sil"
          className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
        {/* Adim cubugu */}
        <nav className="space-y-1" aria-label="Adımlar">
          {rec.steps.map((s) => (
            <button key={s.key} onClick={() => setActive(s.key)}
              className={clsx("flex w-full items-start gap-3 rounded-lg px-3 py-2 text-left transition-colors",
                s.key === step.key ? "bg-indigo-50" : "hover:bg-gray-100")}>
              <StepIcon s={s} current={s.key === rec.current} />
              <span className="min-w-0">
                <span className={clsx("block text-sm font-medium", s.key === step.key ? "text-indigo-700" : "text-gray-900")}>{STEP_META[s.key].n}. {STEP_META[s.key].title}</span>
                <span className="block truncate text-xs text-gray-500">{s.summary}</span>
              </span>
            </button>
          ))}
        </nav>

        {/* Secili adim */}
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="mb-1 flex items-center gap-2">
            <h2 className="text-base font-semibold text-gray-900">{STEP_META[step.key].n}. {STEP_META[step.key].title}</h2>
            <span className={clsx("rounded-full px-2 py-0.5 text-xs font-medium",
              step.status === "done" ? "bg-emerald-50 text-emerald-700" : step.status === "legacy" ? "bg-amber-50 text-amber-700" : step.status === "deferred" ? "bg-sky-50 text-sky-700" : "bg-gray-100 text-gray-600")}>
              {STATUS_LABEL[step.status]}
            </span>
          </div>
          <p className="text-sm text-gray-700">{STEP_META[step.key].what}</p>
          <details className="mt-2 text-sm">
            <summary className="inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-indigo-600"><HelpCircle className="h-3.5 w-3.5" /> Bu adım neden önemli?</summary>
            <p className="mt-1 text-gray-600">{STEP_META[step.key].why}</p>
          </details>

          {step.missing.length > 0 && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="mb-1 text-xs font-semibold text-amber-700">Yapılacaklar</p>
              <ul className="list-disc space-y-0.5 pl-5 text-sm text-amber-700">
                {step.missing.map((m) => <li key={m}>{m}</li>)}
              </ul>
            </div>
          )}

          <div className="mt-5">
            {step.key === "source" && <SourceStep rec={rec} onSaved={refresh} />}
            {step.key === "study" && <StudyStep rec={rec} onChanged={refresh} />}
            {step.key === "template" && <TemplateStep rec={rec} onChanged={refresh} />}
            {step.key === "samples" && <SamplesStep rec={rec} onChanged={refresh} />}
            {step.key === "images" && <ImagesStep rec={rec} onChanged={refresh} />}
            {step.key === "listing" && <ListingStep rec={rec} onChanged={refresh} />}
            {step.key === "publish" && <PublishStep rec={rec} onChanged={refresh} />}
          </div>

          <div className="mt-6 flex items-center justify-between border-t border-gray-100 pt-4">
            <button onClick={() => setActive(STEP_ORDER[idx - 1])} disabled={idx === 0}
              className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-40">← Önceki adım</button>
            <div className="flex items-center gap-2">
              <button onClick={refresh} title="Durumu yenile" aria-label="Durumu yenile" className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"><RefreshCw className="h-4 w-4" /></button>
              {idx < STEP_ORDER.length - 1 && (
                <button onClick={() => setActive(STEP_ORDER[idx + 1])}
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700">
                  Sonraki adım <ArrowRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function StepIcon({ s, current }: { s: StepState; current: boolean }) {
  if (s.status === "done") return <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />;
  if (s.status === "legacy") return <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />;
  if (s.status === "deferred") return <Clock className="mt-0.5 h-4 w-4 flex-shrink-0 text-sky-500" />;
  return <Circle className={clsx("mt-0.5 h-4 w-4 flex-shrink-0", current ? "text-indigo-600" : "text-gray-300")} />;
}

// 1. Kaynak: AI'a gidecek fotograflarin secimi + Etsy kisisellestirme sorulari
function SourceStep({ rec, onSaved }: { rec: CustomListingDetail; onSaved: () => void }) {
  const all = rec.analysis?.imageUrls ?? [];
  const [picked, setPicked] = useState<string[]>(rec.source.imageUrls ?? []);
  const [msg, setMsg] = useState<{ err?: boolean; text: string } | null>(null);
  const dirty = JSON.stringify(picked) !== JSON.stringify(rec.source.imageUrls ?? []);
  const save = useMutation({
    mutationFn: () => customApi("PATCH", `/${rec.id}`, { imageUrls: picked }),
    onSuccess: () => { setMsg({ text: "Seçim kaydedildi." }); onSaved(); },
    onError: (e) => setMsg({ err: true, text: (e as Error).message }),
  });
  const fetchQ = useMutation({
    mutationFn: () => customApi<{ questions: unknown[] }>("POST", `/${rec.id}/fetch-questions`),
    onSuccess: (r) => { setMsg({ text: `Etsy'den ${r.questions.length} soru çekildi.` }); onSaved(); },
    onError: (e) => setMsg({ err: true, text: (e as Error).message }),
  });
  const toggle = (u: string) => setPicked((p) => (p.includes(u) ? p.filter((x) => x !== u) : p.length >= MAX_IMAGES ? p : [...p, u]));
  const questions = rec.source.questions ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-1 text-sm font-semibold text-gray-700">AI'ın inceleyeceği fotoğraflar</h3>
        <p className="mb-3 text-xs text-gray-500">En fazla {MAX_IMAGES}. İlk seçtiğin ana fotoğraf sayılır. Beden tablosu ve mağaza tanıtım görsellerini seçme; renk ve beden bizim varyasyon şablonlarından gelir.</p>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
          {all.map((u) => {
            const i = picked.indexOf(u);
            return (
              <button key={u} onClick={() => toggle(u)} aria-pressed={i >= 0}
                className={clsx("relative aspect-square overflow-hidden rounded-lg border-2 transition-colors", i >= 0 ? "border-indigo-500" : "border-transparent hover:border-gray-300")}>
                <img src={u} alt="" loading="lazy" className="h-full w-full object-cover" />
                {i >= 0 && <span className="absolute left-1 top-1 rounded-full bg-indigo-600 px-1.5 text-xs font-semibold text-white">{i === 0 ? "Ana" : i + 1}</span>}
              </button>
            );
          })}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button onClick={() => save.mutate()} disabled={!dirty || save.isPending || picked.length === 0}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Seçimi kaydet ({picked.length})
          </button>
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-700">İlham listesinin kişiselleştirme soruları</h3>
          <button onClick={() => fetchQ.mutate()} disabled={fetchQ.isPending}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {fetchQ.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} {rec.source.fetchedAt ? "Yeniden çek" : "Etsy'den çek"}
          </button>
        </div>
        <p className="mb-3 text-xs text-gray-500">Etsy API'sinden okunur, sayfa açılmaz. Alıcının ne yazdığını, neyi seçtiğini ve dosya yükleyip yüklemediğini gösterir.</p>
        {questions.length === 0 ? (
          <p className="text-sm text-gray-500">{rec.source.fetchedAt ? "Bu listede kişiselleştirme sorusu yok." : "Henüz çekilmedi."}</p>
        ) : (
          <ol className="space-y-2">
            {questions.map((q, i) => (
              <li key={i} className="rounded-lg border border-gray-200 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">{q.question || "(başlıksız)"}</span>
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{QUESTION_TYPE_LABEL[q.type] ?? q.type}</span>
                  {q.required && <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">Zorunlu</span>}
                  {q.maxChars && <span className="text-xs text-gray-500">en fazla {q.maxChars} karakter</span>}
                  {q.maxFiles && <span className="text-xs text-gray-500">en fazla {q.maxFiles} dosya</span>}
                </div>
                {q.instructions && <p className="mt-1 whitespace-pre-line text-xs text-gray-600">{q.instructions}</p>}
                {q.options && <p className="mt-1 text-xs text-gray-500">Seçenekler: {q.options.join(" · ")}</p>}
              </li>
            ))}
          </ol>
        )}
      </div>

      {msg && <p className={clsx("text-sm", msg.err ? "text-red-600" : "text-emerald-700")}>{msg.text}</p>}
    </div>
  );
}


